import { useState, useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { Box, CircularProgress, Typography, useTheme, useMediaQuery, Button, ButtonGroup } from '@mui/material';
import { tokens } from '../../theme';
import axios from 'axios';

const DecDaoHistoryChart = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullData, setFullData] = useState([]); // Store all data
  const [chartData, setChartData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('30'); // Default 30 days
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Custom theme for the line chart
  const customChartTheme = {
    axis: {
      ticks: {
        text: {
          fill: colors.grey[100],
          fontSize: isMobile ? 9 : 11
        }
      },
      legend: {
        text: {
          fill: colors.grey[100],
          fontSize: isMobile ? 10 : 12
        }
      }
    },
    grid: {
      line: {
        stroke: colors.grey[700],
        strokeWidth: 1
      }
    },
    tooltip: {
      container: {
        background: colors.primary[700],
        color: colors.grey[100],
        fontSize: isMobile ? 10 : 12,
      }
    }
  };

  // Format numbers for display
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  // Format axis values
  const formatAxisValue = (value) => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(0)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toString();
  };

  // Filter data based on selected time range
  const filterDataByTime = (data, days) => {
    if (days === 'all') return data;
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    
    return data.filter(entry => {
      const entryDate = new Date(entry.rawDate);
      return entryDate >= cutoffDate;
    });
  };

  // Fetch DAO balance history
  const fetchDaoHistory = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get('https://api.elmo-burns.com/api/v1/dec-distribution-history/');
      
      if (response.data && response.data.results && response.data.results.length > 0) {
        // Sort by date ascending (oldest to newest)
        const sortedData = [...response.data.results].reverse();
        
        // Calculate DAILY DAO change (difference between consecutive days)
        const allData = [];
        for (let i = 1; i < sortedData.length; i++) {
          const today = sortedData[i];
          const yesterday = sortedData[i - 1];
          
          const dailyChange = parseFloat(today.dao_balance) - parseFloat(yesterday.dao_balance);
          
          allData.push({
            x: today.date,
            y: dailyChange,
            rawDate: today.date
          });
        }
        
        setFullData(allData);
        setError(null);
      } else {
        throw new Error('No DAO history data available');
      }
      
    } catch (err) {
      console.error('Failed to fetch DAO history:', err);
      setError('Unable to load DAO DEC history');
      setFullData([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Update chart data when filter or full data changes
  useEffect(() => {
    if (fullData.length > 0) {
      const days = timeFilter === 'all' ? 'all' : parseInt(timeFilter);
      const filteredData = filterDataByTime(fullData, days);
      
      setChartData([{
        id: 'DAO',
        color: '#e8a838', // Gold color matching previous company chart
        data: filteredData
      }]);
    }
  }, [timeFilter, fullData]);
  
  useEffect(() => {
    fetchDaoHistory();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchDaoHistory, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error" gutterBottom>{error}</Typography>
        <Typography variant="caption" color={colors.grey[400]}>Please try again later</Typography>
      </Box>
    );
  }

  if (chartData.length === 0 || chartData[0].data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color={colors.grey[400]}>No data available yet</Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" position="relative" display="flex" flexDirection="column">
      {/* Time Filter Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ButtonGroup 
          size={isMobile ? "small" : "medium"}
          variant="outlined"
          sx={{
            '& .MuiButton-root': {
              borderColor: colors.grey[700],
              color: colors.grey[100],
              '&:hover': {
                borderColor: '#e8a838',
                backgroundColor: 'rgba(232, 168, 56, 0.1)',
              }
            }
          }}
        >
          <Button
            onClick={() => setTimeFilter('14')}
            sx={{
              backgroundColor: timeFilter === '14' ? 'rgba(232, 168, 56, 0.2)' : 'transparent',
              borderColor: timeFilter === '14' ? '#e8a838' : colors.grey[700],
              fontWeight: timeFilter === '14' ? 'bold' : 'normal',
            }}
          >
            14D
          </Button>
          <Button
            onClick={() => setTimeFilter('30')}
            sx={{
              backgroundColor: timeFilter === '30' ? 'rgba(232, 168, 56, 0.2)' : 'transparent',
              borderColor: timeFilter === '30' ? '#e8a838' : colors.grey[700],
              fontWeight: timeFilter === '30' ? 'bold' : 'normal',
            }}
          >
            30D
          </Button>
          <Button
            onClick={() => setTimeFilter('90')}
            sx={{
              backgroundColor: timeFilter === '90' ? 'rgba(232, 168, 56, 0.2)' : 'transparent',
              borderColor: timeFilter === '90' ? '#e8a838' : colors.grey[700],
              fontWeight: timeFilter === '90' ? 'bold' : 'normal',
            }}
          >
            90D
          </Button>
          <Button
            onClick={() => setTimeFilter('all')}
            sx={{
              backgroundColor: timeFilter === 'all' ? 'rgba(232, 168, 56, 0.2)' : 'transparent',
              borderColor: timeFilter === 'all' ? '#e8a838' : colors.grey[700],
              fontWeight: timeFilter === 'all' ? 'bold' : 'normal',
            }}
          >
            ALL
          </Button>
        </ButtonGroup>
      </Box>
      
      {/* Chart */}
      <Box flex="1" minHeight={0}>
        <ResponsiveLine
          data={chartData}
          theme={customChartTheme}
          margin={{ top: 10, right: isMobile ? 20 : 40, bottom: isMobile ? 40 : 50, left: isMobile ? 40 : 60 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false
          }}
          yFormat=" >-.2f"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: isMobile ? -45 : 0,
            legend: '',
            legendOffset: 36,
            legendPosition: 'middle',
            format: (value) => {
              const date = new Date(value);
              return isMobile ? `${date.getMonth() + 1}/${date.getDate()}` : value;
            }
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendOffset: -50,
            legendPosition: 'middle',
            format: formatAxisValue
          }}
          pointSize={isMobile ? 6 : 8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          curve="monotoneX"
          enableArea={false}
          colors={{ datum: 'color' }}
          lineWidth={3}
          tooltip={({ point }) => (
            <Box
              sx={{
                background: colors.primary[700],
                padding: '9px 12px',
                border: `2px solid #e8a838`,
                borderRadius: '4px',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e8a838' }}>
                {point.data.x}
              </Typography>
              <Typography variant="body2">
                Daily Change: {point.data.y >= 0 ? '+' : ''}{formatNumber(point.data.y)} DEC
              </Typography>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default DecDaoHistoryChart;
